import { Injectable } from '@angular/core';
import { NgxPermissionsService, NgxRolesService } from 'ngx-permissions';
import { camelCase } from 'camel-case';
import { CommonFunctions } from '../utils/common-functions';
import { ClienteService } from './cliente.service';

export type AppRole = 'INNEGOCSA' | 'AB';

export const ROLE_DEFINITIONS: Array<{ role: AppRole, names: string[] }> = [
  { role: 'INNEGOCSA', names: ['AGENTE INNEGOCSA'] },
  { role: 'AB', names: ['AGENTE BANCARIO'] },
];

export const STORAGE_KEYS = {
  roleName: 'roleName',
  roleId: 'roleId',
  permissions: 'permissions',
  authType: 'authType',
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(
    private ngxRoleService: NgxRolesService,
    private ngxPermissionService: NgxPermissionsService,
    private clienteService: ClienteService,
  ) { }

  private normalize(value: string): string {
    if (!value) {
      return '';
    }
    return value
      .normalize('NFD')          
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '') 
      .replace(/\s+/g, ' ')
      .trim();
  }

  resolveRole(roleName: string): AppRole | null {
    const normalized = this.normalize(roleName);
    if (normalized === '') {
      return null;
    }
    const definition = ROLE_DEFINITIONS.find(item =>
      item.names.some(name => {
        const normalizedName = this.normalize(name);
        return normalized === normalizedName || normalized.indexOf(normalizedName) !== -1;
      })
    );
    return definition ? definition.role : null;
  }

  isAllowedRole(roleName: string): boolean {
    return this.resolveRole(roleName) !== null;
  }

  canLoginWithCredentials(roleName: string): boolean {
    return this.resolveRole(roleName) === 'INNEGOCSA';
  }

  canLoginWithToken(roleName: string): boolean {
    return this.resolveRole(roleName) === 'AB';
  }

  getRoleName(): string {
    return localStorage.getItem(STORAGE_KEYS.roleName) || '';
  }

  getAuthType(): AppRole | null {
    const stored = localStorage.getItem(STORAGE_KEYS.authType);
    if (stored === 'INNEGOCSA' || stored === 'AB') {
      return stored;
    }
    return this.resolveRole(this.getRoleName());
  }

  isBankAgent(): boolean {
    return this.getAuthType() === 'AB';
  }

  isInnegocsaAgent(): boolean {
    return this.getAuthType() === 'INNEGOCSA';
  }


  canLogout(): boolean {
    return this.isInnegocsaAgent();
  }

  isAuthenticated(): boolean {
    const roleName = this.getRoleName();
    const roleId = localStorage.getItem(STORAGE_KEYS.roleId);
    if (!roleName || !roleId || !this.isAllowedRole(roleName)) {
      return false;
    }
    return this.getStoredPermissions().length > 0;
  }

  private getStoredPermissions(): any[] {
    try {
      const permissions = JSON.parse(localStorage.getItem(STORAGE_KEYS.permissions) || '[]');
      return Array.isArray(permissions) ? permissions : [];
    } catch (e) {
      return [];
    }
  }

  private toNgxPermissions(permissions: any[]): string[] {
    const ngxPermissions: string[] = [];
    (permissions || []).forEach(permission => {
      if (permission && permission.activo === true) {
        const name = CommonFunctions.cleanSpacesString(permission.nombre_permiso);
        ngxPermissions.push(camelCase(name));
      }
    });
    return ngxPermissions;
  }
  startSession(roleName: string, roleId: any, permissions: any[], authType: AppRole): string[] {
    const ngxPermissions = this.toNgxPermissions(permissions);
    if (ngxPermissions.length === 0) {
      return [];
    }
    localStorage.setItem(STORAGE_KEYS.roleName, roleName);
    localStorage.setItem(STORAGE_KEYS.roleId, roleId.toString());
    localStorage.setItem(STORAGE_KEYS.permissions, JSON.stringify(permissions));
    localStorage.setItem(STORAGE_KEYS.authType, authType);
    this.ngxRoleService.addRole(roleName, ngxPermissions);
    this.ngxPermissionService.addPermission(ngxPermissions);

    this.clienteService.loadSessionData();
    return ngxPermissions;
  }

  restoreSession(): boolean {
    const roleName = this.getRoleName();
    if (!this.isAuthenticated()) {
      return false;
    }
    const ngxPermissions = this.toNgxPermissions(this.getStoredPermissions());
    if (ngxPermissions.length === 0) {
      return false;
    }
    this.ngxRoleService.addRole(roleName, ngxPermissions);
    this.ngxPermissionService.addPermission(ngxPermissions);
    this.clienteService.loadSessionData();
    return true;
  }

  clearSession(): void {
    localStorage.clear();
    this.ngxPermissionService.flushPermissions();
    this.ngxRoleService.flushRolesAndPermissions();
    this.clienteService.loadSessionData();
  }
}
